/**
 * Notification Triggers
 *
 * Fire-and-forget functions that send push notifications AND create
 * in-app notification records for each event type.
 *
 * Tone is determined by onboarding_tier:
 *   - competitor → gritty, direct, competitive pressure
 *   - casual     → encouraging, warm, progress-focused
 */

import { supabase } from '@/lib/supabase';
import { sendPushToUser } from '@/lib/sendPushNotification';
import { createLogger } from '@/lib/logger';
import type { OnboardingTier } from '@/lib/onboardingTiers';

const log = createLogger('NOTIF_TRIGGERS');

type Tone = 'competitor' | 'casual';

// ---------------------------------------------------------------------------
// Tone resolver — cached per user for the session
// ---------------------------------------------------------------------------
const toneCache = new Map<string, { tone: Tone; ts: number }>();
const TONE_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

async function getTone(userId: string): Promise<Tone> {
  const cached = toneCache.get(userId);
  if (cached && Date.now() - cached.ts < TONE_CACHE_TTL) return cached.tone;

  try {
    const { data } = await supabase
      .from('profiles')
      .select('onboarding_tier')
      .eq('id', userId)
      .maybeSingle();
    const tone: Tone = data?.onboarding_tier === 'casual' ? 'casual' : 'competitor';
    toneCache.set(userId, { tone, ts: Date.now() });
    return tone;
  } catch {
    return 'competitor'; // default to gritty
  }
}

function pick(tone: Tone, competitor: string, casual: string): string {
  return tone === 'casual' ? casual : competitor;
}

// ---------------------------------------------------------------------------
// Helper: insert in-app notification record
// ---------------------------------------------------------------------------
async function insertNotification(
  userId: string,
  type: string,
  title: string,
  body: string,
  data: Record<string, any>,
) {
  try {
    await supabase.from('notifications').insert({
      user_id: userId,
      type,
      title,
      body,
      data,
      is_read: false,
    });
  } catch (err) {
    log.error('Insert notification failed:', err);
  }
}

// ---------------------------------------------------------------------------
// Helper: send push + in-app
// ---------------------------------------------------------------------------
async function notify(
  userId: string,
  type: string,
  title: string,
  body: string,
  data: Record<string, any>,
) {
  await Promise.all([
    sendPushToUser(userId, title, body, data),
    insertNotification(userId, type, title, body, data),
  ]);
}

// ---------------------------------------------------------------------------
// 0. Welcome (fire once after signup + onboarding complete)
// ---------------------------------------------------------------------------
export async function notifyWelcome(userId: string): Promise<void> {
  const tone = await getTone(userId);
  const title = pick(tone,
    'You\'re on the board.',
    'Welcome to DUEL! 🏋️',
  );
  const body = pick(tone,
    'Your strength score is live. Find opponents, start a duel, and prove yourself.',
    'Your score is ready! Log workouts, track your progress, and challenge friends.',
  );
  await notify(userId, 'welcome', title, body, { type: 'welcome' });
}

// ---------------------------------------------------------------------------
// 0b. First PR celebration (fire when first PR badge is awarded)
// ---------------------------------------------------------------------------
export async function notifyFirstPR(
  userId: string,
  exerciseName: string,
  weight: number,
  unit: string,
): Promise<void> {
  const tone = await getTone(userId);
  const title = pick(tone,
    `New PR. ${exerciseName}.`,
    `New PR! ${exerciseName} 🎉`,
  );
  const body = pick(tone,
    `${weight}${unit}. That's on the record now. Keep pushing.`,
    `You hit ${weight}${unit} on ${exerciseName}! That's a personal best — you're improving!`,
  );
  await notify(userId, 'pr_celebration', title, body, { type: 'pr_celebration', exerciseName, weight, unit });
}

// ---------------------------------------------------------------------------
// 1. Duel Challenge Sent
// ---------------------------------------------------------------------------
export async function notifyDuelChallenge(
  challengedUserId: string,
  challengerName: string,
  duelType: string,
  duelId: string,
): Promise<void> {
  const tone = await getTone(challengedUserId);
  const title = pick(tone,
    '⚔️ You just got called out.',
    `⚔️ ${challengerName} wants to duel!`,
  );
  const body = pick(tone,
    `${challengerName} just challenged you to a ${duelType} duel. Step up or step aside.`,
    `${challengerName} challenged you to a friendly ${duelType} duel. Tap to accept!`,
  );
  await notify(challengedUserId, 'duel_challenge', title, body, { type: 'duel_challenge', duelId });
}

// ---------------------------------------------------------------------------
// 2. Duel Accepted
// ---------------------------------------------------------------------------
export async function notifyDuelAccepted(
  challengerId: string,
  accepterName: string,
  duelId: string,
): Promise<void> {
  const tone = await getTone(challengerId);
  const title = pick(tone,
    'Challenge accepted.',
    'They\'re in! 🎉',
  );
  const body = pick(tone,
    `${accepterName} isn't backing down. Clock's running — get to work.`,
    `${accepterName} accepted your duel. Time to have some fun and see who comes out on top!`,
  );
  await notify(challengerId, 'duel_accepted', title, body, { type: 'duel_accepted', duelId });
}

// ---------------------------------------------------------------------------
// 3. Opponent Logged a Lift
// ---------------------------------------------------------------------------
export async function notifyOpponentLift(
  opponentId: string,
  opponentName: string,
  exerciseName: string,
  duelId: string,
): Promise<void> {
  const tone = await getTone(opponentId);
  const title = pick(tone,
    'They just moved iron.',
    `${opponentName} just trained!`,
  );
  const body = pick(tone,
    `${opponentName} hit the ${exerciseName}. The gap is closing. Your move.`,
    `${opponentName} logged a ${exerciseName} session. Keep up the momentum!`,
  );
  await notify(opponentId, 'duel_lift', title, body, { type: 'duel_lift', duelId });
}

// ---------------------------------------------------------------------------
// 4. Duel Completed
// ---------------------------------------------------------------------------
// Note: notifyDuelResult (below) fires when the duel is scored/resolved with a winner.
// notifyDuelComplete fires when a duel period expires without resolution (timeout).
// Do NOT call both for the same event.
export async function notifyDuelComplete(
  userId: string,
  opponentName: string,
  duelId: string,
): Promise<void> {
  const tone = await getTone(userId);
  const title = pick(tone,
    'Duel over. Results are in.',
    'Duel complete! 🏁',
  );
  const body = pick(tone,
    `Your battle with ${opponentName} is settled. Check the scoreboard.`,
    `Your duel with ${opponentName} has wrapped up. See how you did!`,
  );
  await notify(userId, 'duel_complete', title, body, { type: 'duel_complete', duelId });
}

// ---------------------------------------------------------------------------
// 5. New Follower
// ---------------------------------------------------------------------------
export async function notifyNewFollower(
  followedUserId: string,
  followerName: string,
  followerId: string,
): Promise<void> {
  const tone = await getTone(followedUserId);
  const title = pick(tone,
    'Someone is watching.',
    'New follower! 👋',
  );
  const body = pick(tone,
    `${followerName} is now following your lifts. Don't embarrass yourself.`,
    `${followerName} started following you. They'll see your progress — keep it going!`,
  );
  await notify(followedUserId, 'new_follower', title, body, { type: 'new_follower', followerId });
}

// ---------------------------------------------------------------------------
// Alias: notifyOpponentLoggedLift (backward compat)
// ---------------------------------------------------------------------------
export async function notifyOpponentLoggedLift(
  opponentId: string,
  loggerName: string,
  duelId: string,
): Promise<void> {
  const tone = await getTone(opponentId);
  const title = pick(tone,
    'They just moved iron.',
    `${loggerName} just trained!`,
  );
  const body = pick(tone,
    `${loggerName} just logged a session. The leaderboard doesn't wait.`,
    `${loggerName} just logged a workout. Your turn!`,
  );
  await notify(opponentId, 'duel_lift', title, body, { type: 'duel_lift', duelId });
}

// ---------------------------------------------------------------------------
// Duel Result (used by duelEloHook)
// ---------------------------------------------------------------------------
export async function notifyDuelResult(
  challengerId: string,
  challengedId: string,
  duelId: string,
  winnerId: string | null,
): Promise<void> {
  const isDraw = winnerId === null;
  for (const userId of [challengerId, challengedId]) {
    const tone = await getTone(userId);
    const isWinner = userId === winnerId;
    const title = isDraw
      ? pick(tone, 'Dead even. Nobody won.', 'It\'s a tie! 🤝')
      : isWinner
        ? pick(tone, 'Victory. Now defend it.', 'You won! 🏆')
        : pick(tone, 'You lost.', 'Tough one. 💪');
    const body = isDraw
      ? pick(tone,
          'The duel ended in a draw. Unacceptable. Challenge again and finish it.',
          'You and your opponent tied! Challenge them again to settle it.',
        )
      : isWinner
        ? pick(tone,
            'Duel closed. Your Elo climbs. Don\'t let it get comfortable up there.',
            'Nice work! Your rating went up. Keep training and keep climbing!',
          )
        : pick(tone,
            'They beat you. Use it. Get back under the bar and come back stronger.',
            'You\'ll get them next time. Every workout makes you stronger for the rematch.',
          );
    await notify(userId, 'duel_result', title, body, { type: 'duel_result', duelId, result: isDraw ? 'draw' : isWinner ? 'win' : 'loss' });
  }
}

// ---------------------------------------------------------------------------
// 6. Weekly Challenge Reminder
// ---------------------------------------------------------------------------
export async function notifyWeeklyChallengeReminder(
  userId: string,
): Promise<void> {
  const tone = await getTone(userId);
  const title = pick(tone,
    'Challenge closes Sunday.',
    'Challenge ending soon! ⏰',
  );
  const body = pick(tone,
    'Clock is running. Submit your score or let someone else take the top spot.',
    'Don\'t forget to submit your weekly challenge score before Sunday!',
  );
  await notify(userId, 'challenge_reminder', title, body, { type: 'challenge_reminder' });
}

// ---------------------------------------------------------------------------
// 7. Streak at Risk
// ---------------------------------------------------------------------------
export async function notifyStreakAtRisk(
  userId: string,
  currentStreak: number,
  daysSinceLastWorkout: number,
): Promise<void> {
  const tone = await getTone(userId);
  const title = pick(tone,
    `${currentStreak}-day streak. Don't blow it.`,
    `${currentStreak}-day streak — keep it alive! 🔥`,
  );
  const body = pick(tone,
    `${daysSinceLastWorkout} days off the bar. One more and the streak is gone. Get in.`,
    `You haven't logged in ${daysSinceLastWorkout} days. One quick session keeps your ${currentStreak}-day streak going!`,
  );
  await notify(userId, 'streak_at_risk', title, body, { type: 'streak_at_risk', currentStreak, daysSinceLastWorkout });
}

// ---------------------------------------------------------------------------
// 8. Lapsed User Re-engagement
// ---------------------------------------------------------------------------
export async function notifyLapsed(
  userId: string,
  daysSinceLastWorkout: number,
): Promise<void> {
  const tone = await getTone(userId);
  const title = pick(tone,
    `${daysSinceLastWorkout} days. Your rank is sliding.`,
    `${daysSinceLastWorkout} days — we saved your spot 💪`,
  );
  const body = pick(tone,
    'Your score is going stale and someone is climbing past you. Get back in the gym.',
    'Your progress is waiting for you. Log a workout anytime to pick up where you left off.',
  );
  await notify(userId, 'we_miss_you', title, body, { type: 'we_miss_you', daysSinceLastWorkout });
}

// ---------------------------------------------------------------------------
// 9. Freshness Decay Warning
// ---------------------------------------------------------------------------
export async function notifyFreshnessDecay(
  userId: string,
  liftName: string,
  daysUntilStale: number,
): Promise<void> {
  const tone = await getTone(userId);
  const title = pick(tone,
    `${liftName} going stale.`,
    `${liftName} needs a refresh 📊`,
  );
  const body = pick(tone,
    `${daysUntilStale} days until your ${liftName} stops counting. Log a set or lose the points.`,
    `Your ${liftName} score will start fading in ${daysUntilStale} days. A quick set keeps it fresh!`,
  );
  await notify(userId, 'freshness_decay', title, body, { type: 'freshness_decay', liftName, daysUntilStale });
}

// ---------------------------------------------------------------------------
// 0c. First Workout (fire when first_workout badge is awarded)
// ---------------------------------------------------------------------------
export async function notifyFirstWorkout(userId: string): Promise<void> {
  const tone = await getTone(userId);
  const title = pick(tone,
    'First blood.',
    'First workout logged! 💪',
  );
  const body = pick(tone,
    'Your first session is on the record. Score updated. Keep stacking.',
    'Great start! Your strength score just updated. Come back tomorrow and watch it climb.',
  );
  await notify(userId, 'first_workout', title, body, { type: 'first_workout' });
}

// ---------------------------------------------------------------------------
// 10. Pro Trial Expiring
// ---------------------------------------------------------------------------
export async function notifyTrialExpiring(
  userId: string,
  hoursRemaining: number,
): Promise<void> {
  const tone = await getTone(userId);
  const daysLeft = Math.ceil(hoursRemaining / 24);
  const title = hoursRemaining <= 24
    ? pick(tone, 'Pro expires tonight.', 'Your Pro trial ends today')
    : pick(tone, `Pro trial ends in ${daysLeft} days.`, `${daysLeft} days left on your Pro trial`);
  const body = hoursRemaining <= 24
    ? pick(tone,
        'Lock it in or lose your analytics, templates, and score history. Your call.',
        'Upgrade now to keep your analytics, templates, and progress charts. We\'d love to have you stay!',
      )
    : pick(tone,
        `${daysLeft} days left on Pro. Keep the tools or go back to guessing.`,
        `${daysLeft} days left on Pro. Enjoying the features? Upgrade anytime to keep them.`,
      );
  await notify(userId, 'trial_expiring', title, body, { type: 'trial_expiring', hoursRemaining });
}
